import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapContainer,
  TileLayer,
  Circle,
  Marker,
  Polyline,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import * as FiIcons from "react-icons/fi";
const {
  FiClock,
  FiActivity,
  FiShield,
  FiTrendingUp,
  FiSettings,
  FiLock,
  FiPlus,
  FiTrash2,
  FiBattery,
} = FiIcons;
import SafeIcon from "../common/SafeIcon";
import api from "../lib/api";
import toast from "react-hot-toast";
import socketManager from "../lib/socket";

const asArray = (value) => (Array.isArray(value) ? value : []);

const parsePoint = (point) => {
  if (Array.isArray(point)) return point.map(Number);
  if (point && typeof point === "object" && "x" in point && "y" in point) {
    return [Number(point.x), Number(point.y)];
  }

  return String(point || "")
    .replace(/[()]/g, "")
    .split(",")
    .map(Number);
};

export default function GeofenceManager({ patientId }) {
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [liveLocation, setLiveLocation] = useState(null);
  const [battery, setBattery] = useState(100);
  const [showAddZone, setShowAddZone] = useState(false);

  useEffect(() => {
    fetchData();

    socketManager.on("location_update", (data) =>
      setLiveLocation({ lat: data.lat, lng: data.lon }),
    );
    socketManager.on(
      "vitals_sync",
      (data) => data.battery !== undefined && setBattery(data.battery),
    );

    return () => {
      socketManager.off("location_update");
      socketManager.off("vitals_sync");
    };
  }, [patientId]);

  const fetchData = async () => {
    if (!patientId) {
      setZones([]);
      setBreadcrumbs([]);
      return;
    }

    try {
      const { data: zRes } = await api.get(
        `/api/v1/analytics/geofence/zones/${patientId}`,
      );
      const nextZones = asArray(zRes);
      setZones(nextZones);
      if (nextZones.length > 0) setSelectedZone(nextZones[0]);

      const { data: bRes } = await api.get(
        `/api/v1/analytics/geofence/history/${patientId}`,
      );
      setBreadcrumbs(asArray(bRes));
    } catch (err) {
      console.error("Geofence fetch error:", err.response?.data || err);
      setZones([]);
      setBreadcrumbs([]);
    }
  };

  const addZone = async (lat, lng) => {
    try {
      const newZone = {
        patient_id: patientId,
        name: `New Zone ${zones.length + 1}`,
        center_point: `(${lat},${lng})`,
        radius_meters: 500,
        is_active: true,
      };
      const { data } = await api.post(
        "/api/v1/analytics/geofence/zones",
        newZone,
      );
      setZones([...zones, data]);
      setSelectedZone(data);
      toast.success("New safety zone added");
      setShowAddZone(false);
    } catch (err) {
      toast.error("Failed to add zone");
    }
  };

  const deleteZone = async (id) => {
    try {
      await api.delete(`/api/v1/analytics/geofence/zones/${id}`);
      setZones(zones.filter((z) => z.id !== id));
      toast.success("Zone removed");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  function MapEvents() {
    useMapEvents({
      click(e) {
        if (showAddZone) addZone(e.latlng.lat, e.latlng.lng);
      },
    });
    return null;
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-white flex items-center gap-2">
          <SafeIcon icon={FiShield} className="text-brand-400" /> Multi-Zone
          Safety
        </h3>
        <div className="flex gap-2">
          <div
            className={`flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold ${battery < 15 ? "bg-rose-500/20 text-rose-400" : "bg-emerald-500/20 text-emerald-400"}`}
          >
            <SafeIcon icon={FiBattery} /> {battery}%
          </div>
          <button
            onClick={() => setShowAddZone(!showAddZone)}
            className={`p-2 rounded-lg transition-all ${showAddZone ? "bg-brand-500 text-white" : "bg-slate-800 text-slate-400"}`}
          >
            <SafeIcon icon={FiPlus} />
          </button>
        </div>
      </div>

      <div className="h-80 rounded-xl overflow-hidden border border-slate-800 relative">
        <MapContainer
          center={liveLocation || [34.0522, -118.2437]}
          zoom={14}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapEvents />

          {zones.map((zone) => {
            const [lat, lng] = parsePoint(zone.center_point);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

            return (
              <React.Fragment key={zone.id}>
                <Marker
                  position={[lat, lng]}
                  icon={L.divIcon({
                    className:
                      "bg-brand-400 w-3 h-3 rounded-full border-2 border-white",
                  })}
                />
                <Circle
                  center={[lat, lng]}
                  radius={zone.radius_meters}
                  pathOptions={{ color: "#6366f1", fillOpacity: 0.05 }}
                />
              </React.Fragment>
            );
          })}

          {breadcrumbs.length > 1 && (
            <Polyline
              positions={breadcrumbs
                .filter((b) => Number.isFinite(b.lat) && Number.isFinite(b.lng))
                .map((b) => [b.lat, b.lng])}
              pathOptions={{ color: "#fb7185", weight: 2, dashArray: "5, 10" }}
            />
          )}

          {liveLocation && (
            <Marker
              position={[liveLocation.lat, liveLocation.lng]}
              icon={L.divIcon({
                className:
                  "bg-brand-500 w-4 h-4 rounded-full border-2 border-white shadow-xl shadow-brand-500/50",
              })}
            />
          )}
        </MapContainer>

        {showAddZone && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-brand-500 text-white px-4 py-2 rounded-full text-xs font-bold shadow-xl animate-bounce">
            Click Map to Add Safety Zone
          </div>
        )}
      </div>

      <div className="space-y-3">
        <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">
          Active Safety Zones
        </p>
        <div className="grid grid-cols-1 gap-2">
          {zones.map((zone) => (
            <div
              key={zone.id}
              className="bg-slate-800/50 p-3 rounded-xl border border-slate-700 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-500/10 flex items-center justify-center text-brand-400">
                  <SafeIcon icon={FiShield} />
                </div>
                <div>
                  <p className="text-white text-xs font-bold">{zone.name}</p>
                  <p className="text-[10px] text-slate-500">
                    {zone.radius_meters}m Radius
                  </p>
                </div>
              </div>
              <button
                onClick={() => deleteZone(zone.id)}
                className="p-2 text-slate-500 hover:text-rose-400 transition-colors"
              >
                <SafeIcon icon={FiTrash2} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-slate-800/50 p-4 rounded-2xl border border-slate-700 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-400">
          <SafeIcon icon={FiTrendingUp} />
        </div>
        <div>
          <p className="text-white text-xs font-bold">AI Pattern Engine</p>
          <p className="text-[10px] text-slate-400">
            Analyzing for wandering & velocity anomalies...
          </p>
        </div>
      </div>
    </div>
  );
}
